import React, { type CSSProperties, FC, useMemo } from 'react'
import moment from 'moment'
import { ICON_REGISTRY } from '@mobileConstants'
import { useExecutePreviewJs } from '@mobileHooks'
import {
  FieldElement,
  IconElement,
  MobileListItem,
  RowElement,
  StyleParameters,
  TemplateElement,
  VectorIcon,
  ViewElement,
} from '@mobileTypes'
import { ChevronRight, Code as CodeIcon } from '@mui/icons-material'
import { Box, Tooltip, Typography } from '@mui/material'

import { DisplayRule } from '@types'

// Constants for styling
const JS_BADGE_COLOR = '#1976d2'
const JS_BADGE_FONT_SIZE = '8px'
const JS_BADGE_MIN_WIDTH = '14px'
const JS_BADGE_HEIGHT = '14px'
const JS_BADGE_PADDING = '0 4px'
const JS_BADGE_BORDER_RADIUS = '7px'
const JS_BADGE_BOX_SHADOW = '0 1px 2px rgba(0,0,0,0.2)'
const CODE_ICON_FONT_SIZE = '16px'
const ICON_DEFAULT_SIZE = 24
const CHEVRON_COLOR = '#5c5c5c'
const CHEVRON_SIZE = 28
const TEXT_SECONDARY_COLOR = '#666'
const ELEMENT_PADDING = '2px 4px'
const ICON_CONTAINER_PADDING = '8px'
const ICON_CONTAINER_BORDER_RADIUS = '8px'
const DEFAULT_ICON_COLOR = '#000000'

type ExecutePreviewJs = ReturnType<typeof useExecutePreviewJs>

type ComputedElementValue = {
  display: string
  error?: string
  tooltip?: string
  rawValue?: any
}

type BaseScriptContext = {
  appData: any
  appConfig: any
  userData: any
  localVariables?: Record<string, any>
  globalVariables?: Record<string, any>
}

enum BORDER_TYPE {
  ELLIPSE = 'ELLIPSE',
  SQUARE = 'SQUARE',
  ROUNDED_SQUARE = 'ROUNDED_SQUARE',
}

type PreviewDisplayRulesMap = Map<number | string, DisplayRule>
type DisplayRuleLike = {
  id?: number | string | null
  internalId?: string | null
  [key: string]: any
}

export interface SingleListItemRendererProps {
  item: Partial<MobileListItem>
  record: any
  recordIndex?: number
  list?: any[]
  commandResultsByKey?: Record<string, any[]>
  commandResultsByObject?: Record<string, any[]>
  displayRulesMap?: PreviewDisplayRulesMap
  showChevron?: boolean
  executeJs?: ExecutePreviewJs
  baseScriptContext?: BaseScriptContext
}

/**
 * Utility function to check if record has data (not empty state)
 */
const hasData = (record: any): boolean => {
  return record && typeof record === 'object' && Object.keys(record).length > 0
}

const resolveDisplayRule = (
  displayRule: DisplayRuleLike | null | undefined,
  displayRulesMap?: PreviewDisplayRulesMap
): DisplayRule | null => {
  if (!displayRule) {
    return null
  }

  const keys = [displayRule.internalId].filter(
    (key): key is string => key !== undefined && key !== null && key !== ''
  )

  for (const key of keys) {
    const fullDisplayRule = displayRulesMap?.get(key)
    if (fullDisplayRule) {
      return fullDisplayRule
    }
  }

  return displayRule as DisplayRule
}

const pathToKeys = (path: string): string[] => {
  return path
    .replace(/\[(\w+)\]/g, '.$1')
    .split('.')
    .filter(Boolean)
}

const getValueByPath = (source: any, path?: string) => {
  if (!source || !path) {
    return undefined
  }

  return pathToKeys(path).reduce((acc: any, key) => {
    if (acc === undefined || acc === null) {
      return undefined
    }

    return acc[key]
  }, source)
}

/**
 * Gets value by pathArray, traversing nested objects
 */
const getValueByPathArray = (
  source: any,
  pathArray?: Array<{ field?: string | null; objectCode?: string | null }> | null,
  commandResultsByObject?: Record<string, any[]>
): any => {
  if (!source || !pathArray || pathArray.length === 0) {
    return undefined
  }

  let currentValue: any = source

  for (let i = 0; i < pathArray.length; i++) {
    const pathItem = pathArray[i]
    if (!pathItem.field) {
      continue
    }

    if (i === 0 && pathItem.objectCode && commandResultsByObject) {
      const objectData = commandResultsByObject[pathItem.objectCode]
      if (objectData && objectData.length > 0) {
        if (currentValue && typeof currentValue === 'object') {
          const refFieldValue = currentValue[pathItem.field]
          if (refFieldValue !== undefined && refFieldValue !== null) {
            const linkedObject = objectData.find(
              (obj: any) => obj.id === refFieldValue || obj.internalId === refFieldValue
            )
            if (linkedObject) {
              currentValue = linkedObject
              continue
            }
          }
        }
      }
    }

    if (currentValue === undefined || currentValue === null) {
      return undefined
    }

    if (Array.isArray(currentValue)) {
      if (currentValue.length > 0) {
        currentValue = currentValue[0][pathItem.field]
      } else {
        return undefined
      }
    } else if (typeof currentValue === 'object') {
      currentValue = currentValue[pathItem.field]
    } else {
      return undefined
    }
  }

  return currentValue
}

const applyFormatDate = (value: any, formatDate?: string | null) => {
  if (!formatDate) {
    return value
  }

  if (value === undefined || value === null || value === '') {
    return value
  }

  const date = moment(value)

  if (!date.isValid()) {
    return value
  }

  return date.format(formatDate)
}

const LENGTH_STYLE_KEYS = new Set([
  'fontSize',
  'lineHeight',
  'borderWidth',
  'borderRadius',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'width',
  'height',
  'minWidth',
  'minHeight',
  'maxWidth',
  'maxHeight',
])

const isNumericLike = (value: unknown): value is number | string => {
  if (typeof value === 'number') {
    return Number.isFinite(value)
  }

  return typeof value === 'string' && /^-?\d+(\.\d+)?(px)?$/i.test(value.trim())
}

const toCssLength = (value: number | string): string => {
  if (typeof value === 'number') {
    return `${value}px`
  }

  const trimmed = value.trim()

  return trimmed.toLowerCase().endsWith('px') ? trimmed : `${trimmed}px`
}

const isLengthStyleKey = (key: string): boolean => {
  return (
    LENGTH_STYLE_KEYS.has(key) ||
    key.startsWith('margin') ||
    key.startsWith('padding') ||
    key.endsWith('Top') ||
    key.endsWith('Right') ||
    key.endsWith('Bottom') ||
    key.endsWith('Left')
  )
}

/**
 * Converts mobile StyleParameters to web CSS while preserving RN-like numeric spacing.
 */
const buildStyleFromParameters = (styleParameters?: StyleParameters): CSSProperties => {
  if (!styleParameters) {
    return {}
  }

  const styles: CSSProperties = {}
  const styleRecord = styles as Record<string, any>

  Object.entries(styleParameters).forEach(([key, rawValue]) => {
    if (rawValue === undefined || rawValue === null || rawValue === '') {
      return
    }

    if (key === 'flex' && isNumericLike(rawValue)) {
      styleRecord[key] =
        typeof rawValue === 'number' ? rawValue : parseFloat(rawValue.replace(/px$/i, ''))

      return
    }

    styleRecord[key] =
      isLengthStyleKey(key) && isNumericLike(rawValue) ? toCssLength(rawValue) : rawValue
  })

  if (styles.borderWidth !== undefined && !styles.borderStyle) {
    styles.borderStyle = 'solid'
  }

  return styles
}

const formatDisplayValue = (value: any): string => {
  if (value === undefined || value === null) {
    return '-'
  }

  if (typeof value === 'string') {
    return value.length > 120 ? `${value.slice(0, 117)}…` : value
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  if (value instanceof Date) {
    return value.toLocaleString()
  }

  if (Array.isArray(value)) {
    if (!value.length) {
      return '[]'
    }

    if (value.length > 3) {
      return `${value.slice(0, 3).join(', ')}…`
    }

    return value.join(', ')
  }

  if (typeof value === 'object') {
    const json = JSON.stringify(value)

    return json.length > 140 ? `${json.slice(0, 137)}…` : json
  }

  return String(value)
}

const computeElementValue = (
  element: RowElement,
  record: any,
  recordIndex: number,
  commandResultsByKey: Record<string, any[]>,
  commandResultsByObject: Record<string, any[]>,
  executeJs: ExecutePreviewJs,
  list: any[],
  baseContext: BaseScriptContext
): ComputedElementValue => {
  if (element.bindType === 'js') {
    const { value, error } = executeJs(element.value, {
      ...baseContext,
      ...commandResultsByKey,
      item: record,
      list,
    })
    const tooltip =
      typeof element.value === 'string' && element.value.trim().length ? element.value : undefined

    return {
      display: formatDisplayValue(value),
      error,
      tooltip,
      rawValue: value,
    }
  }

  if (element.pathArray && element.pathArray.length > 0) {
    const pathArrayValue = getValueByPathArray(record, element.pathArray, commandResultsByObject)

    if (pathArrayValue !== undefined && pathArrayValue !== null && pathArrayValue !== '') {
      const formattedValue = applyFormatDate(pathArrayValue, element.formatDate)

      return {
        display: formatDisplayValue(formattedValue),
        error: undefined,
        rawValue: pathArrayValue,
      }
    }

    if (element.objectCode) {
      const fallbackSource = commandResultsByObject[element.objectCode]?.[0]
      if (fallbackSource) {
        const fallbackValue = getValueByPathArray(
          fallbackSource,
          element.pathArray,
          commandResultsByObject
        )

        if (fallbackValue !== undefined && fallbackValue !== null && fallbackValue !== '') {
          return {
            display: formatDisplayValue(applyFormatDate(fallbackValue, element.formatDate)),
            error: undefined,
            rawValue: fallbackValue,
          }
        }
      }
    }

    return {
      display: '',
      error: undefined,
      rawValue: undefined,
    }
  }

  const baseValue = getValueByPath(record, element.value)

  if (baseValue !== undefined && baseValue !== null && baseValue !== '') {
    const formattedValue = applyFormatDate(baseValue, element.formatDate)

    return {
      display: formatDisplayValue(formattedValue),
      error: undefined,
      rawValue: baseValue,
    }
  }

  if (element.objectCode) {
    const fallbackSource = commandResultsByObject[element.objectCode]?.[0]
    if (fallbackSource) {
      const fallbackValue = getValueByPath(fallbackSource, element.value)

      return {
        display: formatDisplayValue(applyFormatDate(fallbackValue, element.formatDate)),
        error: undefined,
        rawValue: fallbackValue,
      }
    }
  }

  return {
    display: formatDisplayValue(element.value),
    error: undefined,
    tooltip: undefined,
    rawValue: element.value,
  }
}

const styleSettingToCss = (s: any): CSSProperties => {
  const text = s.settingText
  const border = s.settingBorder
  const style: CSSProperties = {
    backgroundColor: s.background || undefined,
    color: text?.fontColor || undefined,
    fontWeight: text?.isBold ? '700' : undefined,
    fontStyle: text?.isItalic ? 'italic' : undefined,
    textAlign: text?.alignment || undefined,
    fontFamily: text?.fontFamily || undefined,
    fontSize: text?.fontSize != null ? toCssLength(text.fontSize) : undefined,
  }
  const styleRecord = style as Record<string, any>

  Object.entries(text || {}).forEach(([key, rawValue]) => {
    if (rawValue === undefined || rawValue === null || rawValue === '') {
      return
    }

    if (key === 'fontColor') {
      style.color = rawValue as string

      return
    }

    if (key === 'alignment') {
      style.textAlign = rawValue as CSSProperties['textAlign']

      return
    }

    if (key === 'isBold' || key === 'isItalic') {
      return
    }

    styleRecord[key] =
      isLengthStyleKey(key) && isNumericLike(rawValue) ? toCssLength(rawValue) : rawValue
  })

  if (border) {
    if (border.color && border.color !== 'none') {
      style.borderColor = border.color
      style.borderWidth = '1px'
      style.borderStyle = 'solid'
    }

    switch (border.type) {
      case BORDER_TYPE.SQUARE:
        style.borderRadius = 0
        break
      case BORDER_TYPE.ROUNDED_SQUARE:
        style.borderRadius = 8
        break
      case BORDER_TYPE.ELLIPSE:
        style.borderRadius = 9999
        break
      default:
        break
    }
  }

  return style
}

const findStyleSettingByKey = (styleSettings: any[] | undefined, key: string) => {
  return styleSettings?.find(s => s?.code === key || s?.internalId === key)
}

const safeExecuteStyleScript = (
  executeJs: ExecutePreviewJs,
  jsSetting: any,
  context: Record<string, unknown>
): string | null => {
  if (!jsSetting) return null

  const script = jsSetting.styleSettingScript?.trim()
  if (!script) return null

  try {
    const { value } = executeJs(script, context)

    return typeof value === 'string' ? value.trim() : null
  } catch (err) {
    console.error('DisplayRule script execution error:', err)

    return null
  }
}

const generateStyleFromDisplayRule = (
  executeJs: ExecutePreviewJs,
  displayRule: DisplayRule | null | undefined,
  context: Record<string, unknown>,
  currentValue?: any
): CSSProperties => {
  if (!displayRule) {
    return {}
  }

  const recordHasData = context && typeof context === 'object' && Object.keys(context).length > 0
  if (!recordHasData) {
    return {}
  }

  const { jsSetting, displayRuleValues } = displayRule

  if (displayRule.isJS) {
    const code = safeExecuteStyleScript(executeJs, jsSetting, context)
    if (!code) {
      return {}
    }

    const matched = findStyleSettingByKey(jsSetting?.styleSettings, code)
    if (!matched) {
      return {}
    }

    return styleSettingToCss(matched)
  }

  if (displayRuleValues) {
    const styleSetting = (displayRuleValues as any[]).find(
      rule => rule.objectFieldValue === currentValue
    )?.styleSetting

    if (!styleSetting) {
      return {}
    }

    return styleSettingToCss(styleSetting)
  }

  return {}
}

/**
 * Component for displaying JS code badge with CodeIcon
 */
interface JSCodeBadgeProps {
  tooltip?: string
  badgePosition?: { top?: number; right?: number }
  containerPadding?: string
}

const JSCodeBadge: FC<JSCodeBadgeProps> = ({
  tooltip,
  badgePosition = { top: -3, right: -5 },
  containerPadding = ELEMENT_PADDING,
}) => {
  const badgeContent = (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        cursor: tooltip ? 'pointer' : 'default',
        padding: containerPadding,
      }}
    >
      <CodeIcon
        sx={{
          fontSize: CODE_ICON_FONT_SIZE,
          color: 'inherit',
          verticalAlign: 'middle',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: badgePosition.top,
          right: badgePosition.right,
          minWidth: JS_BADGE_MIN_WIDTH,
          height: JS_BADGE_HEIGHT,
          padding: JS_BADGE_PADDING,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: JS_BADGE_COLOR,
          borderRadius: JS_BADGE_BORDER_RADIUS,
          boxShadow: JS_BADGE_BOX_SHADOW,
        }}
      >
        <Typography
          sx={{
            fontSize: JS_BADGE_FONT_SIZE,
            fontWeight: 600,
            color: 'white',
            lineHeight: 1,
          }}
        >
          JS
        </Typography>
      </Box>
    </Box>
  )

  return tooltip ? (
    <Tooltip
      title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{tooltip}</pre>}
      placement='top'
      arrow
    >
      {badgeContent}
    </Tooltip>
  ) : (
    badgeContent
  )
}

const renderIcon = (
  icon: MobileListItem['icon'],
  record: any,
  commandResultsByKey: Record<string, any[]>,
  executeJs: ExecutePreviewJs,
  list: any[],
  baseContext: BaseScriptContext,
  displayRulesMap?: PreviewDisplayRulesMap
) => {
  if (!icon) {
    return null
  }

  const recordHasData = hasData(record)

  const fullIconDisplayRule = resolveDisplayRule(icon.displayRule, displayRulesMap)
  const rowContext = { ...baseContext, ...commandResultsByKey, item: record, srcObj: record, list }
  const iconStyles =
    fullIconDisplayRule && recordHasData
      ? generateStyleFromDisplayRule(executeJs, fullIconDisplayRule, rowContext)
      : {}
  const iconStyleParameters =
    typeof icon === 'object' && icon.useStyleParameters !== false
      ? buildStyleFromParameters(icon.styleParameters)
      : {}

  const iconContainerStyles: CSSProperties = {
    padding: fullIconDisplayRule ? ICON_CONTAINER_PADDING : undefined,
    borderRadius: fullIconDisplayRule ? ICON_CONTAINER_BORDER_RADIUS : undefined,
    borderWidth: iconStyles.borderWidth ?? 0,
    borderStyle: iconStyles.borderWidth ? 'solid' : undefined,
    borderColor: iconStyles.borderColor || undefined,
    backgroundColor: iconStyles.backgroundColor || undefined,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...iconStyleParameters,
  }

  const iconColor =
    (iconStyles.color as string) ||
    (icon && 'color' in icon ? icon.color : undefined) ||
    DEFAULT_ICON_COLOR

  if (typeof icon === 'string') {
    return (
      <Box sx={iconContainerStyles}>
        <Typography sx={{ fontSize: `${ICON_DEFAULT_SIZE}px` }}>{icon}</Typography>
      </Box>
    )
  }

  if (icon.isJS) {
    if (!recordHasData) {
      const jsCode = typeof icon.value === 'string' ? icon.value : ''
      const tooltip = jsCode.trim().length > 0 ? jsCode : undefined

      return (
        <Box sx={iconContainerStyles}>
          <JSCodeBadge tooltip={tooltip} />
        </Box>
      )
    }

    const { value, error } = executeJs(icon.value, {
      ...baseContext,
      ...commandResultsByKey,
      item: record,
      list,
    })

    if (error) {
      return (
        <Box sx={iconContainerStyles}>
          <Typography sx={{ fontSize: '11px', color: 'error.main' }}>Icon error</Typography>
        </Box>
      )
    }

    if (!value) {
      return null
    }

    if (typeof value === 'string') {
      return (
        <Box sx={iconContainerStyles}>
          <Typography sx={{ fontSize: '11px' }}>{value}</Typography>
        </Box>
      )
    }

    if (typeof value === 'object') {
      const vectorIcon = value as Partial<VectorIcon>
      const iconItem =
        vectorIcon?.type && vectorIcon?.value
          ? ICON_REGISTRY.find(i => i.id === vectorIcon.value && i.library === vectorIcon.type)
          : null

      if (iconItem) {
        const IconComponent = iconItem.Component

        return (
          <Box sx={iconContainerStyles}>
            <IconComponent
              size={vectorIcon.size ?? ICON_DEFAULT_SIZE}
              color={(vectorIcon as any).color || iconColor}
            />
          </Box>
        )
      }

      return (
        <Box sx={iconContainerStyles}>
          <Typography sx={{ fontSize: '11px', color: 'error.main', textAlign: 'center' }}>
            Undefined Icon
          </Typography>
        </Box>
      )
    }

    return (
      <Box sx={iconContainerStyles}>
        <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>JS Icon</Typography>
      </Box>
    )
  }

  let vectorIcon: VectorIcon | { value: string; size?: number; type?: string } | null = null

  if (icon.value && typeof icon.value === 'string') {
    try {
      const parsed = JSON.parse(icon.value)
      if (parsed && typeof parsed === 'object' && parsed.type && parsed.value) {
        vectorIcon = parsed as VectorIcon
      } else {
        vectorIcon = { value: icon.value, size: icon.size }
      }
    } catch {
      vectorIcon = { value: icon.value, size: icon.size }
    }
  } else {
    vectorIcon = icon as VectorIcon
  }

  let iconItem = null
  if (vectorIcon && vectorIcon.type && vectorIcon.value) {
    iconItem = ICON_REGISTRY.find(i => i.id === vectorIcon!.value && i.library === vectorIcon!.type)
  }

  if (!iconItem && vectorIcon && vectorIcon.value && typeof vectorIcon.value === 'string') {
    iconItem = ICON_REGISTRY.find(i => i.id === vectorIcon!.value)
  }

  if (!iconItem) {
    if (vectorIcon && vectorIcon.value && typeof vectorIcon.value === 'string') {
      return (
        <Box sx={iconContainerStyles}>
          <Typography sx={{ fontSize: `${ICON_DEFAULT_SIZE}px` }}>{vectorIcon.value}</Typography>
        </Box>
      )
    }

    return null
  }

  const IconComponent = iconItem.Component
  const iconSize = (vectorIcon as any)?.size ?? ICON_DEFAULT_SIZE
  const resolvedColor = (vectorIcon as any)?.color ?? iconColor

  return (
    <Box sx={iconContainerStyles}>
      <IconComponent size={iconSize} color={resolvedColor} />
    </Box>
  )
}

/**
 * Common function to render a field element
 */
interface RenderFieldProps {
  element: RowElement
  record: any
  recordIndex: number
  commandDataByKey: Record<string, any[]>
  commandDataByObject: Record<string, any[]>
  executeJs: ExecutePreviewJs
  list: any[]
  baseContext: BaseScriptContext
  displayRulesMap?: PreviewDisplayRulesMap
  styleParameters?: StyleParameters
}

const renderField = ({
  element,
  record,
  recordIndex,
  commandDataByKey,
  commandDataByObject,
  executeJs,
  list,
  baseContext,
  displayRulesMap,
  styleParameters,
}: RenderFieldProps): React.ReactNode => {
  const isJS = element.bindType === 'js'
  const recordHasData = hasData(record)

  if (isJS && !recordHasData) {
    const jsCode = typeof element.value === 'string' ? element.value : ''
    const tooltip = jsCode.trim().length > 0 ? jsCode : undefined

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', bottom: -3 }}>
        <Typography variant='body2' sx={{ fontSize: '11px', lineHeight: 1.4 }}>
          {element.title && <span style={{ color: TEXT_SECONDARY_COLOR }}>{element.title}: </span>}
          <JSCodeBadge
            tooltip={tooltip}
            badgePosition={{ top: -6, right: -10 }}
            containerPadding='0'
          />
        </Typography>
      </Box>
    )
  }

  const {
    display,
    error: elementError,
    tooltip,
    rawValue,
  } = computeElementValue(
    element,
    record,
    recordIndex,
    commandDataByKey,
    commandDataByObject,
    executeJs,
    list,
    baseContext
  )

  const isEmptyValue =
    rawValue == null || rawValue === '' || (typeof rawValue === 'string' && !rawValue.trim())
  if (element.hideWhenEmpty && isEmptyValue) {
    return null
  }

  const fullDisplayRule = resolveDisplayRule(element.displayRule, displayRulesMap)

  const rowContext = { ...baseContext, ...commandDataByKey, item: record, srcObj: record, list }
  const elementStyle: CSSProperties =
    element.useDisplayRule && fullDisplayRule && recordHasData
      ? {
          ...generateStyleFromDisplayRule(executeJs, fullDisplayRule, rowContext, rawValue),
          padding: ELEMENT_PADDING,
        }
      : {}

  const elementStyleParameters = styleParameters ?? element.styleParameters
  const styleParametersStyles =
    element.useStyleParameters === false ? {} : buildStyleFromParameters(elementStyleParameters)
  const combinedStyles = { ...elementStyle, ...styleParametersStyles }

  const displayContent = isJS ? (
    !recordHasData ? (
      <JSCodeBadge tooltip={tooltip} badgePosition={{ top: -6, right: -10 }} containerPadding='0' />
    ) : tooltip ? (
      <Tooltip
        title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{tooltip}</pre>}
        placement='top'
        arrow
      >
        <span
          style={{
            fontWeight: element.title ? 500 : 400,
            ...combinedStyles,
            cursor: 'pointer',
          }}
        >
          {display}
        </span>
      </Tooltip>
    ) : (
      <span style={{ fontWeight: element.title ? 500 : 400, ...combinedStyles }}>{display}</span>
    )
  ) : (
    <span style={{ fontWeight: element.title ? 500 : 400, ...combinedStyles }}>{display}</span>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant='body2' sx={{ fontSize: '11px', lineHeight: 1.4 }}>
        {element.title && <span style={{ color: TEXT_SECONDARY_COLOR }}>{element.title}: </span>}
        {displayContent}
      </Typography>
      {elementError && (
        <Typography variant='caption' color='error.main'>
          {elementError}
        </Typography>
      )}
    </Box>
  )
}

// Render template element (Advanced mode)
const renderTemplateElement = (
  element: TemplateElement,
  record: any,
  commandDataByKey: Record<string, any[]>,
  commandDataByObject: Record<string, any[]>,
  executeJs: ExecutePreviewJs,
  list: any[],
  baseContext: BaseScriptContext,
  displayRulesMap?: PreviewDisplayRulesMap,
  isRootElement = false
): React.ReactNode => {
  if (element.type === 'view') {
    const viewElement = element as ViewElement
    const style = viewElement.style || {}

    const flexValue =
      style.flex !== undefined
        ? typeof style.flex === 'string'
          ? parseFloat(style.flex)
          : style.flex
        : undefined
    const gapValue =
      style.gap !== undefined
        ? (typeof style.gap === 'string' ? parseFloat(style.gap) : style.gap) + 'px'
        : undefined

    const fullDisplayRule = resolveDisplayRule(viewElement.displayRule, displayRulesMap)
    const rowContext = { ...baseContext, ...commandDataByKey, item: record, srcObj: record, list }
    const displayRuleStyles =
      !isRootElement && fullDisplayRule
        ? generateStyleFromDisplayRule(executeJs, fullDisplayRule, rowContext, null)
        : {}
    const styleParametersStyles =
      viewElement.useStyleParameters === false
        ? {}
        : buildStyleFromParameters(viewElement.styleParameters)

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: style.flexDirection || 'column',
          flex: flexValue,
          gap: gapValue,
          flexWrap: style.flexWrap === 'no-wrap' ? 'nowrap' : style.flexWrap,
          justifyContent: style.justifyContent,
          alignItems: style.alignItems,
          textAlign: style.textAlign,
          minWidth: flexValue !== undefined && flexValue > 0 ? 0 : undefined,
          ...styleParametersStyles,
          ...displayRuleStyles,
        }}
      >
        {viewElement.children?.map(child =>
          renderTemplateElement(
            child,
            record,
            commandDataByKey,
            commandDataByObject,
            executeJs,
            list,
            baseContext,
            displayRulesMap,
            false
          )
        )}
      </Box>
    )
  }

  if (element.type === 'icon') {
    const iconElement = element as IconElement
    const iconConfig = {
      value: iconElement.value,
      isJS: iconElement.isJS,
      size: iconElement.size,
      displayRule: iconElement.displayRule,
      useStyleParameters: iconElement.useStyleParameters,
      styleParameters: iconElement.styleParameters,
    }

    const styleParametersStyles =
      iconElement.useStyleParameters === false
        ? {}
        : buildStyleFromParameters(iconElement.styleParameters)

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          ...styleParametersStyles,
        }}
      >
        {renderIcon(
          iconConfig,
          record,
          commandDataByKey,
          executeJs,
          list,
          baseContext,
          displayRulesMap
        )}
      </Box>
    )
  }

  if (element.type === 'field') {
    const fieldElement = element as FieldElement

    const rowElement: RowElement = {
      code: fieldElement.code,
      order: fieldElement.order || 0,
      title: fieldElement.title || '',
      value: fieldElement.value,
      pinnedColumn: fieldElement.pinnedColumn || null,
      objectValue: fieldElement.objectValue || null,
      pathArray: fieldElement.pathArray || null,
      actions: fieldElement.actions || [],
      columnToSort: fieldElement.columnToSort || null,
      objectCode: fieldElement.objectCode,
      valueType: fieldElement.valueType || null,
      fieldType: fieldElement.fieldType || null,
      bindType: fieldElement.bindType || 'field',
      formatDate: fieldElement.formatDate || null,
      editable: fieldElement.editable ?? true,
      enumDescription: fieldElement.enumDescription ?? false,
      visibility: fieldElement.visibility ?? true,
      useLocalParameters: fieldElement.useLocalParameters ?? false,
      useDisplayRule: fieldElement.useDisplayRule ?? false,
      isPk: fieldElement.isPk || null,
      parameters: fieldElement.parameters || [],
      restrictions: fieldElement.restrictions || null,
      valueFormat: fieldElement.valueFormat || null,
      displayRule: fieldElement.displayRule || null,
      preFill: fieldElement.preFill || null,
      hideWhenEmpty: fieldElement.hideWhenEmpty,
      useStyleParameters: fieldElement.useStyleParameters,
      styleParameters: fieldElement.styleParameters,
    }

    return renderField({
      element: rowElement,
      record,
      recordIndex: 0,
      commandDataByKey,
      commandDataByObject,
      executeJs,
      list,
      baseContext,
      displayRulesMap,
      styleParameters:
        fieldElement.useStyleParameters !== false && fieldElement.styleParameters
          ? fieldElement.styleParameters
          : undefined,
    })
  }

  return null
}

/**
 * SingleListItemRenderer - renders a single list item card
 * This component is reusable and can be used independently from MobileListItemPreview
 */
export const SingleListItemRenderer: FC<SingleListItemRendererProps> = ({
  item,
  record,
  recordIndex = 0,
  list = [],
  commandResultsByKey = {},
  commandResultsByObject = {},
  displayRulesMap,
  showChevron = true,
  executeJs: externalExecuteJs,
  baseScriptContext: externalBaseContext,
}) => {
  const internalExecuteJs = useExecutePreviewJs()
  const executeJs = externalExecuteJs || internalExecuteJs

  const baseScriptContext = useMemo<BaseScriptContext>(() => {
    if (externalBaseContext) {
      return externalBaseContext
    }

    return {
      appData: commandResultsByKey,
      appConfig: item,
      userData: {},
    }
  }, [externalBaseContext, commandResultsByKey, item])

  const rows = item.rows || []
  const configMode = item.configMode || 'simple'
  const itemTemplate = item.itemTemplate
  const rootView =
    configMode === 'advanced' && itemTemplate?.children?.[0]?.type === 'view'
      ? (itemTemplate.children[0] as ViewElement)
      : null
  const fullRootDisplayRule = resolveDisplayRule(rootView?.displayRule, displayRulesMap)
  const rootContext = {
    ...baseScriptContext,
    ...commandResultsByKey,
    item: record,
    srcObj: record,
    list,
  }
  const containerDisplayRuleStyles =
    fullRootDisplayRule && hasData(record)
      ? generateStyleFromDisplayRule(executeJs, fullRootDisplayRule, rootContext, null)
      : {}
  const containerStyleParameters =
    item.useStyleParameters === true ? buildStyleFromParameters(item.styleParameters) : {}

  return (
    <Box
      sx={{
        padding: '12px',
        borderRadius: '12px',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        ...containerStyleParameters,
        ...containerDisplayRuleStyles,
      }}
    >
      {configMode === 'advanced' && itemTemplate ? (
        <>
          {itemTemplate.children.length > 0 ? (
            itemTemplate.children.map((child, index) => (
              <React.Fragment key={index}>
                {renderTemplateElement(
                  child,
                  record,
                  commandResultsByKey,
                  commandResultsByObject,
                  executeJs,
                  list,
                  baseScriptContext,
                  displayRulesMap,
                  index === 0
                )}
              </React.Fragment>
            ))
          ) : (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant='body2' color='text.secondary'>
                Нет настроенных элементов для отображения.
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <>
          {item.icon && (
            <Box sx={{ flexShrink: 0 }}>
              {renderIcon(
                item.icon,
                record,
                commandResultsByKey,
                executeJs,
                list,
                baseScriptContext,
                displayRulesMap
              )}
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            {rows.length > 0 &&
              rows.map((row, rowIndex) => (
                <Box
                  key={rowIndex}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'wrap',
                  }}
                >
                  {row.elements.map((element, elementIndex) => (
                    <Box key={elementIndex} sx={{ display: 'flex', flexDirection: 'column' }}>
                      {renderField({
                        element,
                        record,
                        recordIndex,
                        commandDataByKey: commandResultsByKey,
                        commandDataByObject: commandResultsByObject,
                        executeJs,
                        list,
                        baseContext: baseScriptContext,
                        displayRulesMap,
                        styleParameters:
                          element.useStyleParameters !== false && element.styleParameters
                            ? element.styleParameters
                            : undefined,
                      })}
                    </Box>
                  ))}
                </Box>
              ))}
          </Box>
        </>
      )}
      {showChevron && configMode !== 'advanced' && (
        <Box sx={{ flexShrink: 0 }}>
          <ChevronRight sx={{ color: CHEVRON_COLOR, fontSize: CHEVRON_SIZE }} />
        </Box>
      )}
    </Box>
  )
}

export default SingleListItemRenderer
